/* Import External Modules */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

/* Import Leverege Modules */
import { Flex, Button, Content, Label, Text, Checkbox } from '@leverege/ui-elements';
import { TitleBar } from '@leverege/ui-plugin';
import { UI } from '@leverege/ui-redux';
import I18N from '@leverege/i18n';
import { capitalize } from '@leverege/string-util';

/* Import Local Modules */
import S from './LogUploadForm.css';
import Uploader from '../../uploader/Uploader';
import LogService from '../../services/recovr/LogService';
import { LOG_TYPES, SKU_TYPES, DEFAULT_TAG_SKU } from '../../constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('recovr.logUpload');
/**
 * Log upload form.
 */
class LogUploadForm extends React.Component {
  static propTypes = {
    titleVariant: PropTypes.string,
    layoutVariant: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    titleIcon: PropTypes.string,
    submitText: PropTypes.string,
    cancelText: PropTypes.string,
    loading: PropTypes.bool,
    skuType: PropTypes.string.isRequired,
    logType: PropTypes.string.isRequired,
    revert: PropTypes.bool
  };
  static defaultProps = {
    titleVariant: 'logUploadDialogTitle',
    layoutVariant: 'logUploadDialogTitle',
    title: null,
    titleIcon: null,
    submitText: null,
    cancelText: 'Cancel',
    loading: false,
    revert: false
  };
  constructor(props) {
    super(props);
    const {
      logType,
      skuType
    } = props;
    this.isManufacturing = logType === LOG_TYPES.MANUFACTURING || logType === LOG_TYPES.ARCHIVE;
    this.state = {
      sku: '',
      sampleAlias: '',
      csvLoading: false,
      submitLoading: false,
      setSimOrganization: !!this.isManufacturing,
      activateSim: !this.isManufacturing,
      tagSimCards: false
    };
    this.hasSimSku = skuType === SKU_TYPES.LOCATOR && logType !== LOG_TYPES.ARCHIVE || skuType === SKU_TYPES.GATEWAY && logType !== LOG_TYPES.ARCHIVE;
  }
  onSubmit = async event => {
    const {
      sku,
      file,
      sampleAlias,
      setSimOrganization,
      activateSim,
      tagSimCards
    } = this.state;
    const simOptions = {
      setSimOrganization,
      activateSim,
      tagSimCards,
      hasSimSku: this.hasSimSku
    };
    const {
      actions,
      onClose,
      logType,
      revert
    } = this.props;
    this.setState({
      submitLoading: true
    });
    await LogService.upload({
      logType,
      sku,
      file,
      actions,
      onClose,
      revert,
      sampleAlias,
      simOptions
    });
    this.setState({
      submitLoading: false
    });
  };
  onCancel = event => {
    const {
      onClose,
      eventData
    } = this.props;
    onClose?.({
      data: eventData,
      original: event
    });
  };
  onCSVSelect = async event => {
    const {
      dispatch,
      logType
    } = this.props;
    const {
      file
    } = event.value;
    this.setState({
      csvLoading: true
    });
    const {
      sku,
      sampleAlias
    } = await this.extractSkuFromFile(file, logType);
    this.setState({
      file,
      sku,
      sampleAlias,
      csvLoading: false
    });
    dispatch(UI.set('uploadedFileName', file.name));
  };

  // Read CSV file as text
  readFileAsText = async file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error(t('fileError')));
      reader.readAsText(file);
    });
  };

  // Extract SKU from file
  extractSkuFromFile = async file => {
    const {
      logType,
      skuType
    } = this.props;
    if (!file) {
      throw new Error(t('noFileError'));
    }
    try {
      const text = await this.readFileAsText(file);
      const sku = logType === LOG_TYPES.MANUFACTURING || logType === LOG_TYPES.ARCHIVE ? await this.getManufacturingLogSku(text) : skuType;
      const sampleAlias = logType === LOG_TYPES.SHIPMENT ? await this.getSampleAliasFromCSV(text, skuType) : null;
      return {
        sku,
        sampleAlias
      };
    } catch (error) {
      throw new Error(`${t('skuError')} ${error.message}`);
    }
  };
  getSampleAliasFromCSV = async (text, skuType) => {
    const lines = text.split(/\r\n|\n/);
    const firstLine = lines[0].toLowerCase();
    const values = firstLine.split(skuType === 'tag' ? '.' : ',');
    return values[1];
  };
  getManufacturingLogSku = async text => {
    const lines = text.split(/\r\n|\n/);
    const firstLine = lines[0].toLowerCase();
    // Check if the CSV has headers
    const containsHeaders = firstLine.includes('macaddress') || firstLine.includes('devicesn');
    let sku;
    const values = firstLine.split(',');
    if (!containsHeaders) {
      // Regex to find model and revision in the first line if no headers
      const modelRegEx = /^hw\d{4}$/;
      const modelValue = values.find(value => modelRegEx.test(value));
      const revisionRegEx = /^\d+\.\d+$/;
      let revisionValue = values.find(value => revisionRegEx.test(value));
      // Hack for backwards compatibility with old tag logs that had 13 as their revision
      if (!revisionValue) {
        if (modelValue?.toUpperCase() === DEFAULT_TAG_SKU.model) {
          revisionValue = DEFAULT_TAG_SKU.revision;
        }
      }
      if (modelValue && revisionValue) {
        sku = `${modelValue?.toUpperCase()}-${revisionValue}`;
      }
    }
    // Determine SKU from second line if the CSV contains headers
    if (containsHeaders && lines.length > 1) {
      const secondLine = lines[1];
      const modelIndex = values.indexOf('hwmodel');
      const revisionIndex = values.indexOf('hwrevision');
      const secondLineValues = secondLine.split(',');
      if (modelIndex > -1 && revisionIndex > -1) {
        sku = secondLineValues[modelIndex]?.toUpperCase() === DEFAULT_TAG_SKU.model ? DEFAULT_TAG_SKU.value : `${secondLineValues[modelIndex]?.toUpperCase()}-${secondLineValues[revisionIndex]}`;
      }
    }
    return sku;
  };
  render() {
    const {
      titleVariant,
      layoutVariant,
      title: rawTitle,
      titleIcon: rawTitleIcon,
      submitText: rawSubmitText,
      cancelText,
      loading,
      logType,
      revert
    } = this.props;
    const {
      file,
      sku,
      csvLoading,
      submitLoading,
      setSimOrganization,
      activateSim,
      tagSimCards
    } = this.state;
    const revertText = t('revert');
    const uploadText = t('upload');
    const title = rawTitle || `${revert ? revertText : uploadText} ${capitalize(logType)} Log`;
    const titleIcon = rawTitleIcon || revert ? tIcon('undoIcon') : tIcon('uploadIcon');
    const submitText = rawSubmitText || revert ? revertText : uploadText;
    return /*#__PURE__*/_jsxs(Content, {
      relative: true,
      children: [/*#__PURE__*/_jsx(Content.Header, {
        variant: "formHeader",
        children: /*#__PURE__*/_jsx(TitleBar, {
          layoutVariant: layoutVariant,
          variant: titleVariant,
          title: title,
          icon: titleIcon
        })
      }), /*#__PURE__*/_jsxs(Content.Area, {
        variant: "formBody",
        className: S.form,
        children: [/*#__PURE__*/_jsxs(Flex, {
          variant: "colM",
          children: [csvLoading && this.isManufacturing && /*#__PURE__*/_jsx(Label, {
            class: "loadingDots",
            variant: "inputTitle",
            children: t('skuAnalyze')
          }), /*#__PURE__*/_jsx(Uploader, {
            onChange: this.onCSVSelect,
            onClose: this.onCancel,
            file: file
          }), !file && /*#__PURE__*/_jsx(Label, {
            class: S.logLabel,
            variant: "logUploadSupported",
            children: t('supported')
          }), !csvLoading && sku?.length > 1 && this.isManufacturing && /*#__PURE__*/_jsxs(Label, {
            class: S.logLabel,
            children: [t('detected'), " ", sku]
          }), !csvLoading && !sku?.length && file && this.isManufacturing && /*#__PURE__*/_jsx(Text, {
            class: S.logLabelError,
            children: t('noSkuDetected')
          })]
        }), !revert && this.hasSimSku ? /*#__PURE__*/_jsxs(Flex, {
          variant: "colL",
          children: [/*#__PURE__*/_jsx(Flex, {
            variant: "row",
            children: /*#__PURE__*/_jsx(Checkbox, {
              label: t('setSimOrganization'),
              value: setSimOrganization,
              onChange: ({
                value
              }) => this.setState(s => ({
                ...s,
                setSimOrganization: value
              }))
            })
          }), /*#__PURE__*/_jsx(Flex, {
            variant: "row",
            children: /*#__PURE__*/_jsx(Checkbox, {
              label: t('activateSim'),
              value: activateSim,
              onChange: ({
                value
              }) => this.setState(s => ({
                ...s,
                activateSim: value
              }))
            })
          }), !this.isManufacturing ? /*#__PURE__*/_jsx(Flex, {
            variant: "row",
            children: /*#__PURE__*/_jsx(Checkbox, {
              label: t('tagSimCards'),
              value: tagSimCards,
              onChange: ({
                value
              }) => this.setState(s => ({
                ...s,
                tagSimCards: value
              }))
            })
          }) : null]
        }) : null]
      }), /*#__PURE__*/_jsxs(Content.Footer, {
        variant: "formButtons",
        layout: "flex:rowMEnd",
        children: [/*#__PURE__*/_jsx(Button, {
          variant: "secondary",
          type: "button",
          onClick: this.onCancel,
          children: loading ? /*#__PURE__*/_jsx("i", {
            className: "fa fa-spin fa-spinner"
          }) : cancelText
        }), /*#__PURE__*/_jsx(Button, {
          variant: "primary",
          disabled: !file || !sku,
          onClick: this.onSubmit,
          children: submitLoading ? /*#__PURE__*/_jsx("i", {
            className: "fa fa-spin fa-spinner"
          }) : submitText
        })]
      })]
    });
  }
}

/* Export Module */
export default connect((state, props) => {
  return {};
})(LogUploadForm);